import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import DateSelector from '@/components/selectors/DateSelector';
import TimeSelector from '@/components/selectors/TimeSelector';
import { useSettingsStore } from '@core/store';

interface DateTimeRangeSelectorProps {
    startDate: DateTime;
    endDate: DateTime;
    startTime: DateTime;
    endTime: DateTime;
    onStartDateChange: (date: DateTime) => void;
    onEndDateChange: (date: DateTime) => void;
    onStartTimeChange: (time: DateTime) => void;
    onEndTimeChange: (time: DateTime) => void;
    maxRange?: number;
    locale?: string;
    error?: boolean;
    helperText?: string;
    startTimeMax?: DateTime;
    endTimeMin?: DateTime;
    endTimeMax?: DateTime;
    today?: DateTime;
}

const DateTimeRangeSelector = ({
    startDate,
    endDate,
    startTime,
    endTime,
    onStartDateChange,
    onEndDateChange,
    onStartTimeChange,
    onEndTimeChange,
    error,
    helperText,
    startTimeMax,
    endTimeMin,
    endTimeMax,
    today = DateTime.local()
}: DateTimeRangeSelectorProps) => {
    const { t } = useTranslation();
    const { isMobile } = useSettingsStore();

    const startDateStr = startDate?.toISODate() || null;
    const endDateStr = endDate?.toISODate() || null;
    const todayDt = today;

    const handleStartDateChange = (isoDate: string) => {
        const dt = DateTime.fromISO(isoDate);
        if (dt.isValid) onStartDateChange(dt);
    };

    const handleEndDateChange = (isoDate: string) => {
        const dt = DateTime.fromISO(isoDate);
        if (dt.isValid) onEndDateChange(dt);
    };

    const handleStartTimeChange = (time: string) => {
        const dt = DateTime.fromISO(time);
        if (dt.isValid) {
            onStartTimeChange(dt);
            return;
        }
        const parts = time.split(':');
        if (parts.length === 2) {
            const result = startDate.set({
                hour: parseInt(parts[0]),
                minute: parseInt(parts[1])
            });
            if (result.isValid) onStartTimeChange(result);
        }
    };

    const handleEndTimeChange = (time: string) => {
        const dt = DateTime.fromISO(time);
        if (dt.isValid) {
            onEndTimeChange(dt);
            return;
        }
        const parts = time.split(':');
        if (parts.length === 2) {
            const result = endDate.set({
                hour: parseInt(parts[0]),
                minute: parseInt(parts[1])
            });
            if (result.isValid) onEndTimeChange(result);
        }
    };

    const startTimeStr = startTime?.isValid
        ? `${String(startTime.hour).padStart(2, '0')}:${String(startTime.minute).padStart(2, '0')}`
        : '';
    const endTimeStr = endTime?.isValid
        ? `${String(endTime.hour).padStart(2, '0')}:${String(endTime.minute).padStart(2, '0')}`
        : '';

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                flex: isMobile ? '1 1 100%' : '0 0 auto',
                width: isMobile ? '100%' : 'auto'
            }}
        >
            {/* Start Date */}
            <Box
                sx={{
                    width: isMobile ? 'calc(65% - 8px)' : 200,
                    flex: isMobile ? '0 0 calc(65% - 8px)' : '0 0 200px',
                    boxSizing: 'border-box',
                    minWidth: 0
                }}
            >
                <DateSelector
                    value={startDateStr}
                    onChange={handleStartDateChange}
                    label={t('startDate')}
                    maxDate={endDate?.isValid ? endDate : todayDt}
                />
                {helperText && <Box className={`selector-helper ${error ? 'error' : ''}`}>{helperText}</Box>}
            </Box>

            <Box
                sx={{
                    width: isMobile ? 'calc(35% - 8px)' : 130,
                    flex: isMobile ? '0 0 calc(35% - 8px)' : '0 0 107px',
                    boxSizing: 'border-box',
                    minWidth: 0
                }}
            >
                <TimeSelector
                    value={startTimeStr}
                    onChange={handleStartTimeChange}
                    label={t('startTime')}
                    maxTime={startTimeMax}
                    minutesStep={5}
                    sx={{ width: '100% !important', maxWidth: '100% !important' }}
                />
            </Box>

            {/* End Date */}
            <Box
                sx={{
                    width: isMobile ? 'calc(665% - 8px)' : 200,
                    flex: isMobile ? '0 0 calc(65% - 8px)' : '0 0 200px',
                    boxSizing: 'border-box',
                    minWidth: 0
                }}
            >
                <DateSelector
                    value={endDateStr}
                    onChange={handleEndDateChange}
                    label={t('endDate')}
                    minDate={startDate?.isValid ? startDate : undefined}
                    maxDate={todayDt}
                />
                {helperText && <Box className={`selector-helper ${error ? 'error' : ''}`}>{helperText}</Box>}
            </Box>

            {/* End Time — minTime: start time + 1min when same day */}
            <Box
                sx={{
                    width: isMobile ? 'calc(35% - 8px)' : 130,
                    flex: isMobile ? '0 0 calc(35% - 8px)' : '0 0 107px',
                    boxSizing: 'border-box',
                    minWidth: 0
                }}
            >
                <TimeSelector
                    value={endTimeStr}
                    onChange={handleEndTimeChange}
                    label={t('endTime')}
                    minTime={endTimeMin}
                    maxTime={endTimeMax}
                    minutesStep={5}
                    sx={{ width: '100% !important', maxWidth: '100% !important' }}
                />
            </Box>
        </Box>
    );
};

export default DateTimeRangeSelector;
